// Lib imports
import Vue from "vue";
import VueRouter from "vue-router";

// App imports
import api from "@/api/cloud";
import store from "@/store";

// Routes
import isRouteForbidden from "./helpers/isRouteForbidden";
import patchRouter from "./monkey-patch";
import paths from "./paths";

// Grab environment query param, if present
let environment = new URL(location.href).searchParams.get("environment");

Vue.use(VueRouter);

const router = patchRouter(
	new VueRouter({
		mode: "history",
		base: process.env.BASE_URL,
		routes: paths,
	})
);

// Helper to automatically update active community selection
async function setActiveCommunity(communityId) {
	
	console.log(`setActiveCommunity ${communityId}`)
	// Always make sure that communities are loaded
	await store.dispatch("communities/load");

	const activeCommunityId = store.state.session.communityId;

	if (communityId !== activeCommunityId) {

		const community = store.getters["communities/itemById"](communityId);

		await store.dispatch("session/setCommunityId", { id: community.id, operator: community.operator });

		// Community has changed: selected system is no longer valid
		if (store.state.session.systemId) {
			store.commit("session/setSystemId", null);
		}
	}
}

// Helper to automatically update active system selection
async function setActiveSystem(systemId) {
	
	console.log(`setActiveSystem ${systemId}`)
	// Always make sure that communities are loaded
	await store.dispatch("communities/load");

	// Get associated community for the given system
	const system = await api.getSystem(systemId);

	// Set session's active community
	await setActiveCommunity(system._communityId);

	const community = store.getters["communities/itemById"](system._communityId);

	// Load systems for given community
	await store.dispatch("rooms/load", community);

	// // Update session's active system
	const activeSystemId = store.state.session.systemId;

	if (systemId !== activeSystemId) {
		store.commit("session/setSystemId", systemId);
	}
}

async function setActiveUser(userId)
{
	console.log(`setActiveUser ${userId}`)
	const activeCommunityId = store.state.session.communityId;

	const user = await api.getUser(userId);

	//try to use active if they have it, if not, take first
	const community = _.find(user.communities, ['_id', activeCommunityId]) || _.head(user.communities);
	await setActiveCommunity(community._id);

}

async function getLoginRedirect(to) {
	const isLogin = to.matched.some(route => route.name === "Login");
	const isAuthed = store.getters["auth/signedIn"];

	if (!isLogin && !isAuthed) {
		try {
			await store.dispatch("auth/getCurrentUser");
		} catch (e) {
			return {
				name: "Login",
				query: { redirectFrom: to.fullPath },
			};
		}
	}

	return null;
}

async function syncVuexWithRoute(to, from, next) {
	try {
		if (to.params.systemId) await setActiveSystem(to.params.systemId);
		else if (to.params.communityId) await setActiveCommunity(to.params.communityId);
		else if (to.params.userId) await setActiveUser(to.params.userId);
	} catch (e) {
		console.error("Failed!", e);
		next(e);
		return true;
	}
}

function checkRouteAuth(to, from, next) {
	let reason = isRouteForbidden(to, next);
	if (reason) {
		window.vm.$log(`Failed to navigate to (${to.path}): ${reason}`);

		// Perform some cleanup...
		store.commit("session/setCommunityId", null);
		store.commit("session/setSystemId", null);

		// Redirect to forbidden page.
		next({ name: "Forbidden" });
		return true;
	}
}

router.beforeEach(async (to, from, next) => {
	// Preserve environment query param
	if (environment && to.query.environment !== environment) {
		return next({
			...to,
			query: { ...to.query, environment }
		});
	}

	// If we're not on login page and not signed in, go to login page
	const loginRedirect = await getLoginRedirect(to);
	if (loginRedirect) {
		return next(loginRedirect);
	}

	next();
});

router.beforeResolve(async (to, from, next) => {
	// Automatically update *active* community and system from route params
	if (await syncVuexWithRoute(to, from, next)) return;

	// Make sure we have authorization to navigate to specified page
	if (checkRouteAuth(to, from, next)) return;

	next();
});

router.afterEach(async (to, from) => {
	// Dynamically set vuetify theme based on route
	const isDark = to.meta.theme === "dark";
	router.app.$vuetify.theme.dark = isDark;
});

// Vue Router 3 treats guard redirects as errors. Suppress them.
const originalPush = router.push.bind(router);
const originalReplace = router.replace.bind(router);

function handleNavError(err) {
  // Ignore intentional redirects and duplicate navigations
  if (err?.name === 'NavigationDuplicated') return;
  if (err?.message?.includes('Redirected when going from')) return;
  throw err;
}

router.push = (location) => originalPush(location).catch(handleNavError);
router.replace = (location) => originalReplace(location).catch(handleNavError);

export default router;
